import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useDispatch, useSelector } from "react-redux";
import { easing } from "maath";
import { useGSAP } from "@gsap/react";
import {
  setActiveCamera,
  setCurrentState,
  setInTrans,
  stateMachine,
  clearSkipIntroFlag,
} from "../../store/reducers/progressionReducer";
import gsap from "gsap";
import { PerspectiveCamera } from "@react-three/drei";
import { LoopOnce, Quaternion } from "three";
import controlsStyle from "../../UI/Controls/Controls.module.css";

export const CameraController = forwardRef(function ({ actions, mixer }, ref) {
  const { camera } = useThree();
  const currentModule = useSelector((state) => state.progression.currentModule);
  const [isMobile, setIsMobile] = useState(false);
  const currentPos = useRef({ x: 0, y: 1.9, z: 10 });
  const mobilePos = useRef({ x: 0, y: 1.7, z: 12 });
  const currentRot = useRef(new THREE.Euler(0, 0, 0));
  const pointer = useRef({ x: 0, y: 0 });
  const dispatch = useDispatch();
  const nextState = useSelector((state) => state.progression.nextState);
  const inTrans = useSelector((state) => state.progression.inTrans);
  const token = useSelector((state) => state.progression.token);
  const activeCamera = useSelector((state) => state.progression.activeCamera);
  const currentState = useSelector((state) => state.progression.currentState);
  const skipIntroAnimation = useSelector((state) => state.progression.skipIntroAnimation);
  const [initialized, setInitialized] = useState(false);
  const [lastRestoredState, setLastRestoredState] = useState(null);
  const introPlayed = useRef(false);

  useEffect(() => {
    const handleMouseMove = (event) => {
      pointer.current.x = (event.clientX / window.innerWidth) * 2 - 1;
      pointer.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    const handleTouchMove = (event) => {
      if (event.touches.length > 0) {
        pointer.current.x = (event.touches[0].clientX / window.innerWidth) * 2 - 1;
        pointer.current.y = -(event.touches[0].clientY / window.innerHeight) * 2 + 1;
      }
    };

    function resize() {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      if (mobile) {
        adjustCameraForMobile();
      } else {
        adjustCameraForDesktop();
      }
    }

    resize();

    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("touchmove", handleTouchMove);

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  const adjustCameraForMobile = () => {
    if (!inTrans && activeCamera !== "IntroCamera") {
      const currentStateName = nextState !== "none" ? nextState : activeCamera;
      const currentState = stateMachine[currentStateName];

      if (currentState && currentState.mobileCamera) {
        const mobileConfig = currentState.mobileCamera;

        gsap.to(camera.position, {
          x: mobileConfig.position[0],
          y: mobileConfig.position[1],
          z: mobileConfig.position[2],
          duration: 0.5,
          ease: "power2.out",
          onComplete: () => {
            currentPos.current = {
              x: mobileConfig.position[0],
              y: mobileConfig.position[1],
              z: mobileConfig.position[2]
            };
            mobilePos.current = { ...currentPos.current };
          }
        });

        if (mobileConfig.quaternion && Array.isArray(mobileConfig.quaternion) && mobileConfig.quaternion.length === 4) {
          const mobileQuat = new Quaternion(
            mobileConfig.quaternion[0],
            mobileConfig.quaternion[1],
            mobileConfig.quaternion[2],
            mobileConfig.quaternion[3]
          );
          const initialQuat = new Quaternion(
            camera.quaternion.x,
            camera.quaternion.y,
            camera.quaternion.z,
            camera.quaternion.w
          );

          const animation = gsap.to({}, {
            duration: 0.5,
            ease: "circ.out",
            onUpdate: function () {
              camera.quaternion.slerpQuaternions(initialQuat, mobileQuat, animation.progress());
            },
            onComplete: function () {
              const finalRot = camera.rotation;
              currentRot.current = new THREE.Euler(finalRot.x, finalRot.y, finalRot.z);
            }
          });
        }
      } else {
        gsap.to(camera.position, {
          x: mobilePos.current.x,
          y: mobilePos.current.y,
          z: mobilePos.current.z,
          duration: 0.5,
          ease: "power2.out",
          onComplete: () => {
            currentPos.current = { ...mobilePos.current };
          }
        });
      }
    }
  };

  const adjustCameraForDesktop = () => {
    const desktopState = stateMachine[nextState !== "none" ? nextState : activeCamera];
    const desktopPos = desktopState ? desktopState.camera.position : [0, 1.9, 10];

    if (!inTrans && activeCamera !== "IntroCamera") {
      gsap.to(camera.position, {
        x: desktopPos[0],
        y: desktopPos[1],
        z: desktopPos[2],
        duration: 0.5,
        ease: "power2.out",
        onComplete: () => {
          currentPos.current = {
            x: desktopPos[0],
            y: desktopPos[1],
            z: desktopPos[2]
          };
        }
      });
    }
  };

  // Initialiser la caméra à la bonne position au montage selon l'état Redux
  useEffect(() => {
    // Ne s'exécuter qu'une seule fois après le montage
    if (initialized) return;
    
    try {
      if (token && activeCamera !== "IntroCamera" && currentState && stateMachine[currentState]) {
        // Si l'utilisateur est connecté et qu'on a une caméra active (pas IntroCamera)
        const stateConfig = stateMachine[currentState];
        if (!stateConfig) {
          console.warn("stateConfig non trouvé pour:", currentState);
          setInitialized(true);
          return;
        }
        
        const cameraConfig = isMobile && stateConfig.mobileCamera
          ? stateConfig.mobileCamera
          : stateConfig.camera;
        
        if (cameraConfig && 
            Array.isArray(cameraConfig.position) && 
            cameraConfig.position.length === 3 &&
            Array.isArray(cameraConfig.quaternion) && 
            cameraConfig.quaternion.length === 4) {
          
          camera.position.set(cameraConfig.position[0], cameraConfig.position[1], cameraConfig.position[2]);
          const quat = new THREE.Quaternion(
            cameraConfig.quaternion[0],
            cameraConfig.quaternion[1],
            cameraConfig.quaternion[2],
            cameraConfig.quaternion[3]
          );
          camera.quaternion.copy(quat);
          
          currentPos.current = {
            x: cameraConfig.position[0],
            y: cameraConfig.position[1],
            z: cameraConfig.position[2]
          };
          mobilePos.current = { ...currentPos.current };
          currentRot.current = new THREE.Euler().setFromQuaternion(quat);
          
          console.log("Caméra initialisée à:", currentState, cameraConfig.position);
          setLastRestoredState(currentState);
        }
      }
    } catch (error) {
      console.error("Erreur lors de l'initialisation de la caméra:", error);
    } finally {
      setInitialized(true);
    }
  }, [camera, token, activeCamera, currentState, isMobile, initialized]);

  // Repositionner la caméra si currentState change après restauration (retour de new-product par exemple)
  useEffect(() => {
    console.log("🎥 CameraController - currentState changed:", currentState, {
      initialized,
      token: !!token,
      inTrans,
      activeCamera,
      lastRestoredState,
      hasStateMachine: !!stateMachine[currentState]
    });
    
    if (!initialized || !token || inTrans || activeCamera === "IntroCamera") {
      console.log("❌ Repositionnement annulé - conditions non remplies");
      return;
    }
    if (currentState === lastRestoredState) {
      console.log("❌ Repositionnement annulé - déjà à cette position");
      return;
    }
    if (!currentState || !stateMachine[currentState]) {
      console.log("❌ Repositionnement annulé - état invalide");
      return;
    }
    
    // Si currentState a changé et qu'on n'est pas en transition, repositionner immédiatement
    const stateConfig = stateMachine[currentState];
    const cameraConfig = isMobile && stateConfig.mobileCamera
      ? stateConfig.mobileCamera
      : stateConfig.camera;
    
    if (cameraConfig && 
        Array.isArray(cameraConfig.position) && 
        cameraConfig.position.length === 3 &&
        Array.isArray(cameraConfig.quaternion) && 
        cameraConfig.quaternion.length === 4) {
      
      console.log("✅ Repositionnement immédiat de la caméra vers:", currentState, cameraConfig.position);
      
      camera.position.set(cameraConfig.position[0], cameraConfig.position[1], cameraConfig.position[2]);
      const quat = new THREE.Quaternion(
        cameraConfig.quaternion[0],
        cameraConfig.quaternion[1],
        cameraConfig.quaternion[2],
        cameraConfig.quaternion[3]
      );
      camera.quaternion.copy(quat);
      
      currentPos.current = {
        x: cameraConfig.position[0],
        y: cameraConfig.position[1],
        z: cameraConfig.position[2]
      };
      mobilePos.current = { ...currentPos.current };
      currentRot.current = new THREE.Euler().setFromQuaternion(quat);
      
      setLastRestoredState(currentState);
    }
  }, [currentState, initialized, token, inTrans, activeCamera, camera, isMobile, lastRestoredState]);

  useEffect(() => {
    // Ne jouer l'animation intro qu'une seule fois par session
    if (!token || introPlayed.current) {
      console.log("🎬 Animation intro ignorée - token:", !!token, "introPlayed:", introPlayed.current);
      return;
    }
    
    // Si skipIntroAnimation est true, c'est qu'on revient de new-product
    console.log("🎬 Vérification animation intro:", {
      currentState,
      skipIntroAnimation
    });
    
    if (skipIntroAnimation) {
      console.log("⏭️ Animation intro sautée - retour depuis new-product vers:", currentState);
      introPlayed.current = true;
      
      // Afficher les controls immédiatement puisqu'on ne joue pas l'intro
      gsap.to(`.${controlsStyle.controls}`, {
        opacity: 1,
        pointerEvents: "auto",
        duration: 0.5,
        ease: "power2.out",
      });
      
      // S'assurer que la caméra est active
      dispatch(setActiveCamera("MainCamera"));
      
      // Réinitialiser le flag après usage
      dispatch(clearSkipIntroFlag());
    } else {
      console.log("▶️ Lancement de l'animation intro (nouvelle visite)");
      introPlayed.current = true;
      initCamera();
    }
    
    return () => {
      mixer.removeEventListener("finished", endAnim);
    }
  }, [token, skipIntroAnimation])

  useFrame((state, delta) => {
    if (!inTrans && pointer.current && activeCamera !== "IntroCamera") {
      const pointerEffect = isMobile ? 0.1 : 0.5;
      const pointerXEffect = isMobile ? 0.05 : 0.2;

      easing.damp2(
        camera.position,
        [
          Math.sin(pointer.current.x / 2) * -1 * pointerEffect + currentPos.current.x,
          Math.sin(pointer.current.y / 2) * 0.2 * pointerEffect + currentPos.current.y,
          currentPos.current.z,
        ],
        0.5,
        delta,
      );
      easing.damp2(
        camera.rotation,
        [
          currentRot.current.x,
          Math.sin(pointer.current.x / -2) * pointerXEffect + currentRot.current.y,
          currentRot.current.z,
        ],
        0.5,
        delta,
      );
    }
  });

  useGSAP(
    () => {
      if (nextState !== "none" && stateMachine[nextState]) {
        // Vérifier que l'état existe dans stateMachine
        const stateConfig = stateMachine[nextState];
        if (!stateConfig || !stateConfig.camera) {
          console.error("Configuration de caméra non trouvée pour l'état:", nextState);
          return;
        }

        dispatch(setInTrans(true));

        const cameraConfig = isMobile && stateConfig.mobileCamera
          ? stateConfig.mobileCamera
          : stateConfig.camera;

        // Vérifier que cameraConfig a les bonnes propriétés
        if (!cameraConfig || !Array.isArray(cameraConfig.position) || !Array.isArray(cameraConfig.quaternion)) {
          console.error("Configuration de caméra invalide pour:", nextState, cameraConfig);
          dispatch(setInTrans(false));
          return;
        }

        const pos = cameraConfig.position;
        const quat = new Quaternion(
          cameraConfig.quaternion[0],
          cameraConfig.quaternion[1],
          cameraConfig.quaternion[2],
          cameraConfig.quaternion[3]
        );
        const initialQuat = new Quaternion(
          camera.quaternion.x,
          camera.quaternion.y,
          camera.quaternion.z,
          camera.quaternion.w
        );

        gsap.to(camera.position, {
          x: pos[0],
          y: pos[1],
          z: pos[2],
          duration: 1.25,
          ease: "back.out(1.4)",
          onComplete: () => {
            dispatch(setInTrans(false));
            dispatch(setCurrentState(nextState));
            currentPos.current = {
              x: pos[0],
              y: pos[1],
              z: pos[2],
            };
            mobilePos.current = {
              x: pos[0],
              y: pos[1],
              z: pos[2],
            };
          },
        });

        const animation = gsap.to(camera.rotation, {
          duration: 0.75,
          ease: "circ.out",
          onUpdate: function () {
            camera.quaternion.slerpQuaternions(initialQuat, quat, animation.progress())
          },
          onComplete: function () {
            const finalRot = camera.rotation;
            currentRot.current = new THREE.Euler(finalRot.x, finalRot.y, finalRot.z);
          }
        });
      }
    },
    { dependencies: [nextState, isMobile] },
  );

  function initCamera() {
    const action = actions.CameraAction;

    if (action) {
      action.clampWhenFinished = true;
      action.loop = LoopOnce;
      action.time = 0;
      action.play();
      action._clip.duration = 170 / 30;
      action.paused = true;

      if (token) {
        setTimeout(() => {
          action.paused = false;
        }, 300);

        mixer.addEventListener("finished", endAnim);
      }
    } else {
      console.error("CameraAction is undefined");
    }
  }

  function endAnim() {
    console.log("🎬 endAnim appelé - currentState actuel:", currentState);
    dispatch(setActiveCamera("MainCamera"));
    
    // Ne changer l'état que si on n'a pas déjà un état restauré
    // Si currentState est déjà défini et différent de IntroCamera, le conserver
    if (currentState === "IntroCamera" || !currentState) {
      console.log("📍 Passage à Welcome (pas d'état restauré)");
      dispatch(setCurrentState("Welcome"));
    } else {
      console.log("📍 Conservation de l'état restauré:", currentState);
      // L'état est déjà restauré, ne pas le changer
    }
    
    gsap.to(`.${controlsStyle.controls}`, {
      opacity: 1,
      pointerEvents: "auto",
      duration: 1,
      ease: "power2.out",
    })
  }

  return (
    <PerspectiveCamera
      makeDefault
      name={"Camera"}
      far={100}
      fov={isMobile ? 80 : 50.269}
      position={[0, 1.9, isMobile ? 9 : 7]}
      ref={ref}
    />
  );
});

export default CameraController;
